/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { prefetchSequenceCommits } from '@/lib/queryPrefetch'
import type { Sequence } from '@/types/database'

/**
 * Hook to get or create the default sequence for a project
 * For MVP, we use a single "Main Timeline" sequence per project
 * OPTIMIZED: Prefetches commits when sequence is loaded
 */
export function useDefaultSequence(projectId: string | undefined) {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: ['sequences', 'default', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')

      // Try to find existing default sequence
      const { data: existingSequences, error: fetchError } = await supabase
        .from('sequences')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true })
        .limit(1)

      if (fetchError) throw fetchError

      let sequence: Sequence | null = null

      // If sequence exists, return it
      if (existingSequences && existingSequences.length > 0) {
        sequence = existingSequences[0] as Sequence
      } else {
        // Otherwise, create a default sequence
        const { data: newSequence, error: createError } = await supabase
          .from('sequences')
          .insert([
            {
              project_id: projectId,
              name: 'Main Timeline',
              custom_fields: {
                is_default: true,
              },
            },
          ])
          .select()
          .maybeSingle()

        if (createError) {
          throw createError
        }

        if (!newSequence) {
          throw new Error('Failed to create sequence')
        }

        sequence = newSequence as Sequence
      }

      // OPTIMIZATION: Prefetch commits in the background
      // This reduces loading time when navigating to commits tab
      if (sequence?.id) {
        prefetchSequenceCommits(queryClient, sequence.id).catch((err) => {
          // Don't fail the sequence query if prefetch fails
          console.warn('Failed to prefetch commits:', err)
        })
      }

      return sequence
    },
    enabled: !!projectId,
  })
}

/**
 * Hook to fetch all sequences for a project
 */
export function useSequences(projectId: string | undefined) {
  return useQuery({
    queryKey: ['sequences', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')

      const { data, error } = await supabase
        .from('sequences')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as Sequence[]
    },
    enabled: !!projectId,
  })
}
